import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { CreateNote } from '@src/features/EidSearch/CreateNote/CreateNote';
import { DeleteNote } from '@src/features/EidSearch/DeleteNote/DeleteNote';
import { EditNote } from '@src/features/EidSearch/EditNote/EditNote';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { config } from '@uhg-abyss/web/tools/config';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import { isEmpty, isNil, isUndefined } from 'lodash';
import React, { useState } from 'react';

import {
  AssignTags,
  AssociatedEids,
  AttachActionPaths,
  CoverageStatus,
  RiskCode,
  RiskPublishDate,
  RiskScore,
} from './components';

/**
 * Details
 *
 * Displays a summary of the EID search results.
 *
 * @param actionPaths
 * @param activeInfo
 * @param associatedEids
 * @param eid
 * @param handleSearch
 * @param ireRiskRecord
 * @param note
 * @param riskCodes
 * @param riskRecord
 * @param tags
 * @returns {Element}
 * @constructor
 */
export function Details({
  actionPaths = [],
  activeInfo = {},
  associatedEids = [],
  eid = '',
  handleSearch = () => {},
  ireRiskRecord = {},
  note = '',
  riskCodes = [],
  riskRecord = {},
  tags = [],
}) {
  const [isAssociatedEidsOpen, setIsAssociatedEidsOpen] = useState(false);
  const [isAttachActionPathsOpen, setIsAttachActionPathsOpen] = useState(false);
  const [isAssignTagsOpen, setIsAssignTagsOpen] = useState(false);
  const [isCreateNoteOpen, setIsCreateNoteOpen] = useState(false);
  const [isEditNoteOpen, setIsEditNoteOpen] = useState(false);
  const [isDeleteNoteOpen, setIsDeleteNoteOpen] = useState(false);

  const { hasPermission } = useCurrentUser();

  const canAttachActionPaths = hasPermission('ActionPaths', 'attach');
  const canAssignTags = hasPermission('Analysis:EidSearch', 'assignTags');

  const HeaderItem = styled('div', {
    '&:not(:last-child)': {
      borderRight: '1px solid var(--abyss-colors-gray3)',
    },
    minWidth: '200px',
  });

  const noteContent = (
    <React.Fragment>
      <p>{note}</p>
      <Flex direction="row" justify="space-between">
        <Button
          onClick={() => {
            return setIsEditNoteOpen(true);
          }}
          rounded
          size="$sm"
          variant="outline"
        >
          <IconSymbol icon="edit" isScreenReadable title="edit" />
        </Button>
        <Button
          onClick={() => {
            return setIsDeleteNoteOpen(true);
          }}
          rounded
          size="$sm"
          variant="destructive"
        >
          <IconSymbol icon="delete" isScreenReadable title="delete" />
        </Button>
      </Flex>
    </React.Fragment>
  );

  return (
    <ErrorHandler location="src/pages/private/Analysis/EidSearch/components/Details/Details.jsx">
      <Box color="$tint1" css={{ border: '1px solid #e6e8eb', borderRadius: '4px', padding: 'var(--abyss-space-lg)' }}>
        <Flex
          css={{
            'abyss-flex-root': {
              rowGap: '24px',
            },
          }}
          justify="center"
        >
          <HeaderItem>
            <Layout.Stack alignLayout="center">
              <Text size="$sm">EID</Text>
              {isEmpty(eid) ? <Text color="$gray5">&mdash;</Text> : <Text fontWeight="bold">{eid}</Text>}
            </Layout.Stack>
          </HeaderItem>
          <HeaderItem>
            <Layout.Stack alignLayout="center">
              <Text size="$sm">Risk Score</Text>
              <div>
                <RiskScore ireRiskRecord={ireRiskRecord} riskRecord={riskRecord} />
              </div>
            </Layout.Stack>
          </HeaderItem>
          <HeaderItem>
            <Layout.Stack alignLayout="center">
              <Text size="$sm">Risk Code</Text>
              <div>
                <RiskCode ireRiskRecord={ireRiskRecord} riskCodes={riskCodes} riskRecord={riskRecord} />
              </div>
            </Layout.Stack>
          </HeaderItem>
          <HeaderItem>
            <Layout.Stack alignLayout="center">
              <Text size="$sm">Risk Publish Date</Text>
              <div>
                <RiskPublishDate ireRiskRecord={ireRiskRecord} riskRecord={riskRecord} />
              </div>
            </Layout.Stack>
          </HeaderItem>
          <HeaderItem>
            <Layout.Stack alignLayout="center">
              <Text size="$sm">Coverage Status</Text>
              <div>
                <CoverageStatus activeInfo={activeInfo} />
              </div>
            </Layout.Stack>
          </HeaderItem>

          {note && (
            <HeaderItem className="test">
              <Layout.Stack alignLayout="center" css={{ height: '100%', justifyContent: 'center', margin: '0' }}>
                <Popover
                  align="center"
                  content={
                    <pre
                      style={{
                        fontFamily: 'var(--abyss-fonts-primary)',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                      }}
                    >
                      {noteContent}
                    </pre>
                  }
                  hasCustomButtonTrigger
                  position="bottom"
                >
                  <Button
                    after={<IconSymbol icon="assignment" isScreenReadable title="assignment" />}
                    size="$sm"
                    variant="outline"
                  >
                    Note
                  </Button>
                </Popover>
              </Layout.Stack>
            </HeaderItem>
          )}
          <HeaderItem>
            <Flex
              alignContent="center"
              alignItems="center"
              css={{ height: '100%' }}
              direction="column"
              justify="center"
            >
              <DropdownMenu
                after={<IconSymbol icon="keyboard_arrow_down" />}
                label="Actions"
                menuItems={[
                  {
                    icon: <IconSymbol icon="open_in_new" variant="outlined" />,
                    onClick: () => {
                      window?.open(`${config('HCM_URL')}/${eid}/`, '_blank').focus();
                    },
                    title: 'Link to HCM',
                  },
                  {
                    disabled: isUndefined(associatedEids) || isEmpty(associatedEids),
                    icon: <IconSymbol icon="visibility" variant="outlined" />,
                    onClick: () => {
                      setIsAssociatedEidsOpen(true);
                    },
                    title: "View Associated EID's",
                  },
                  {
                    disabled: !canAttachActionPaths,
                    icon: <IconSymbol icon="attach_file" variant="outlined" />,
                    onClick: () => {
                      setIsAttachActionPathsOpen(true);
                    },
                    title: 'Attach Action Paths',
                  },
                  {
                    disabled: !canAssignTags,
                    icon: <IconSymbol icon="sell" variant="outlined" />,
                    onClick: () => {
                      setIsAssignTagsOpen(true);
                    },
                    title: 'Assign Tags',
                  },
                  {
                    disabled: !isNil(note) && !isEmpty(note),
                    icon: <IconSymbol icon="assignment" variant="outlined" />,
                    onClick: () => {
                      setIsCreateNoteOpen(true);
                    },
                    title: 'Create Note',
                  },
                ]}
                outline
              />
            </Flex>
          </HeaderItem>
        </Flex>
      </Box>

      {isAssociatedEidsOpen && (
        <AssociatedEids
          associatedEids={associatedEids}
          isOpen={isAssociatedEidsOpen}
          setIsOpen={setIsAssociatedEidsOpen}
        />
      )}

      {canAttachActionPaths && isAttachActionPathsOpen && (
        <AttachActionPaths
          eid={eid}
          handleSearch={handleSearch}
          isOpen={isAttachActionPathsOpen}
          recordActionPaths={actionPaths}
          setIsOpen={setIsAttachActionPathsOpen}
        />
      )}

      {canAssignTags && isAssignTagsOpen && (
        <AssignTags
          eid={eid}
          handleSearch={handleSearch}
          isOpen={isAssignTagsOpen}
          recordTags={tags}
          setIsOpen={setIsAssignTagsOpen}
        />
      )}

      {isCreateNoteOpen && (
        <CreateNote
          eid={eid}
          handleSearch={handleSearch}
          isOpen={isCreateNoteOpen}
          note={note}
          setIsOpen={setIsCreateNoteOpen}
        />
      )}

      {isEditNoteOpen && (
        <EditNote
          eid={eid}
          handleSearch={handleSearch}
          isOpen={isEditNoteOpen}
          note={note}
          setIsOpen={setIsEditNoteOpen}
        />
      )}

      {isDeleteNoteOpen && (
        <DeleteNote
          eid={eid}
          handleSearch={handleSearch}
          isOpen={isDeleteNoteOpen}
          note={note}
          setIsOpen={setIsDeleteNoteOpen}
        />
      )}
    </ErrorHandler>
  );
}
